// Gemini Adapter for Unified LLM Interface

/**
 * Convert unified messages to Gemini format
 */
const convertMessagesToGemini = (messages) => {
  const contents = []
  const systemMessages = []

  for (const msg of messages) {
    if (msg.role === 'system') {
      systemMessages.push(msg.content)
    } else if (msg.role === 'user') {
      contents.push({ role: 'user', parts: [{ text: msg.content }] })
    } else if (msg.role === 'assistant') {
      const parts = []
      if (msg.content && msg.content !== 'null') {
        parts.push({ text: msg.content })
      }
      if (msg.toolCalls) {
        msg.toolCalls.forEach((tc) => {
          try {
            parts.push({
              functionCall: {
                name: tc.function.name,
                args: JSON.parse(tc.function.arguments),
              },
            })
          } catch (e) {
            console.error('[Gemini] Failed to parse tool call arguments:', {
              toolName: tc.function.name,
              arguments: tc.function.arguments,
            })
            throw e
          }
        })
      }
      if (parts.length > 0) {
        contents.push({ role: 'model', parts })
      }
    } else if (msg.role === 'tool') {
      // Gemini API: function responses use role "user" with functionResponse part
      // https://ai.google.dev/gemini-api/docs/function-calling
      try {
        let parsedContent = JSON.parse(msg.content)
        // Gemini requires response to be a Struct (object with named fields)
        // Primitives and arrays must be wrapped
        if (typeof parsedContent !== 'object' || parsedContent === null || Array.isArray(parsedContent)) {
          parsedContent = { result: parsedContent }
        }
        contents.push({
          role: 'user',
          parts: [
            {
              functionResponse: {
                name: msg.toolName,
                response: parsedContent,
              },
            },
          ],
        })
      } catch (e) {
        console.error('[Gemini] Failed to parse tool response:', {
          toolName: msg.toolName,
          content: msg.content,
        })
        throw e
      }
    }
  }

  const systemInstruction =
    systemMessages.length > 0
      ? { parts: [{ text: systemMessages.join('\n\n') }] }
      : null

  return { contents, systemInstruction }
}

/**
 * Create Gemini adapter
 * @param {Object} geminiClient - Gemini client instance
 * @returns {Object} Adapter with chat and listModels methods
 */
export function createGeminiAdapter(geminiClient) {
  const chat = async ({
    apiKey,
    model = 'gemini-2.5-flash',
    messages,
    tools,
    config = {},
  }) => {
    const { contents, systemInstruction } = convertMessagesToGemini(messages)

    const geminiTools = tools
      ? [
          {
            functionDeclarations: tools.map((tool) => ({
              name: tool.name,
              description: tool.description,
              parameters: tool.parameters,
            })),
          },
        ]
      : undefined

    const generationConfig = {}
    if (config.temperature !== undefined)
      generationConfig.temperature = config.temperature
    if (config.maxTokens !== undefined)
      generationConfig.maxOutputTokens = config.maxTokens
    if (config.topP !== undefined) generationConfig.topP = config.topP
    if (config.topK !== undefined) generationConfig.topK = config.topK

    const response = await geminiClient.generateContent({
      apiKey,
      model,
      contents,
      tools: geminiTools,
      toolConfig: config.toolChoice
        ? { functionCallingConfig: { mode: config.toolChoice } }
        : undefined,
      systemInstruction,
      generationConfig:
        Object.keys(generationConfig).length > 0 ? generationConfig : undefined,
    })

    if (!response.candidates || response.candidates.length === 0) {
      throw new Error(
        `No candidates in Gemini response: ${JSON.stringify(response)}`,
      )
    }

    const candidate = response.candidates[0]

    if (
      !candidate.content ||
      !candidate.content.parts ||
      candidate.content.parts.length === 0
    ) {
      throw new Error(
        `Gemini returned empty response. FinishReason: ${candidate.finishReason}. ` +
          `This usually indicates an issue with the conversation format or tool responses.`,
      )
    }

    const parts = candidate.content.parts

    const result = {
      role: 'assistant',
      finishReason: candidate.finishReason,
      usage: {
        promptTokens: response.usageMetadata?.promptTokenCount,
        completionTokens: response.usageMetadata?.candidatesTokenCount,
        totalTokens: response.usageMetadata?.totalTokenCount,
      },
    }

    const textPart = parts.find((p) => p.text)
    if (textPart) {
      result.content = textPart.text
    }

    const functionCalls = parts.filter((p) => p.functionCall)
    if (functionCalls.length > 0) {
      result.toolCalls = functionCalls.map((fc, index) => ({
        id: `call_${Date.now()}_${index}`,
        type: 'function',
        function: {
          name: fc.functionCall.name,
          arguments: JSON.stringify(fc.functionCall.args),
        },
      }))
    }

    return { message: result, raw: response }
  }

  const listModels = async ({ apiKey }) => {
    return await geminiClient.listModels({ apiKey })
  }

  return { chat, listModels }
}
